import { Component, computed, OnInit } from "@angular/core";
import { Area, JobIssue } from "../../../../../core/database/db";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { SkeletonModule } from "primeng/skeleton";
import { Bq9DataService } from "../services/bq9-data.service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import {
  AreaIssuesListComponent,
  AreaWithIssues, ViewIssueEvent
} from "../../../../../domains/areas/components/area-issues-list/area-issues-list.component";
import { UNASSIGNED_AREA } from "../../../../../domains/areas/models/AreaDTO";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";

import { ButtonModule } from "primeng/button";
import { DataUpdatedFadeDirective } from "../../../../../shared/directives/data-updated-fade.directive";

@Component({
  selector: "app-bq9-celebration",
  imports: [ScheduleDetailHeader, SkeletonModule, AreaIssuesListComponent, ButtonModule, RouterOutlet, DataUpdatedFadeDirective],
  templateUrl: "./bq9-celebration.component.html",
  styleUrl: "./bq9-celebration.component.scss"
})
export class Bq9CelebrationComponent implements OnInit {
  subtitle = "";
  dataLoaded = false;
  issuesSavingMap = computed(() => this.bq9DataService.issuesSavingMap());
  isRefreshingInBackground = computed(() => this.bq9DataService.isRefreshingInBackground());
  dataUpdatedPulse = computed(() => this.bq9DataService.dataUpdatedPulse());

  readonly areasWithIssues = computed<AreaWithIssues[]>(() => {
    const areas = this.bq9DataService.areas() ?? [];
    const issues = this.bq9DataService.jobIssues() ?? [];

    const areaIdentifierToAreaMap = new Map<string, Area>();
    for (const area of areas) {
      const identifier = area.identifier;
      if (!identifier) {
        continue;
      }
      areaIdentifierToAreaMap.set(identifier, area);
    }

    const areaIdentifiersToIssueMap = new Map<string, JobIssue[]>();
    for (const issue of issues) {
      const areaIdentifier = issue.lotAreaIdentifier || UNASSIGNED_AREA.identifier;
      if (!areaIdentifiersToIssueMap.has(areaIdentifier)) {
        areaIdentifiersToIssueMap.set(areaIdentifier, []);
      }
      areaIdentifiersToIssueMap.get(areaIdentifier)!.push(issue);
    }

    return Array.from(areaIdentifiersToIssueMap, ([identifier, issues]) => ({
      id: areaIdentifierToAreaMap.get(identifier)?.id || UNASSIGNED_AREA.id,
      name: areaIdentifierToAreaMap.get(identifier)?.name || UNASSIGNED_AREA.name,
      issues
    })).sort((a, b) => {
      if (a.name === UNASSIGNED_AREA.name) {
        return -1;
      }
      if (b.name === UNASSIGNED_AREA.name) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });
  });

  readonly canReviewAndSign = computed(() => {
    const issues = this.bq9DataService.jobIssues();
    if (!issues) {
      return false;
    }

    return issues.every(issue => issue.isConfirmedByHomeowner || !!issue.postCompleteStatus);
  });

  constructor(
    private bq9DataService: Bq9DataService,
    private toastService: UiToastService,
    private router: ExtendedRouter,
    private route: ActivatedRoute
  ) { }

  async ngOnInit(): Promise<void> {
    const lot = this.bq9DataService.lot();
    const areas = this.bq9DataService.areas();
    const issues = this.bq9DataService.jobIssues();

    const hasData = !!lot && !!areas && !!issues;
    if (hasData === false) {
      this.toastService.error("Error loading job data for BQ9. Please try again.");
      this.goBack();
      return;
    }

    if (lot) {
      this.subtitle = `Lot ${lot.lotBlock} - ${lot.streetAddress}`;
    }

    this.dataLoaded = true;
  }

  getBackLabel(): string {
    return this.bq9DataService.fromLotSummary() ? "Lot" : "Schedule";
  }

  goBack(): void {
    const lot = this.bq9DataService.lot();
    if (this.bq9DataService.fromLotSummary() && lot) {
      this.router.navigateWithPersistedState(["/cm/all-my-lots/community", lot.externalFinancialCommunityId, "lot", lot.id, "summary"]);
    } else {
      this.router.navigateWithPersistedState(["/cm/schedule"]);
    }
  }

  viewIssue(event: ViewIssueEvent): void {
    const basePath = `area/${event.area.id}/issue/${event.issue.id}`;
    const path = event.issue.postCompleteStatus ? `${basePath}/edit` : basePath;
    this.router.navigateWithPersistedState([path], {
      relativeTo: this.route
    });
  }

  getViewIssuesUrl(area: AreaWithIssues, issue: JobIssue): string {
    const basePath = `area/${area.id}/issue/${issue.id}`;
    const path = issue.postCompleteStatus ? `${basePath}/edit` : basePath;
    return this.router.serializeUrl(
      this.router.createUrlTree([path], {
        relativeTo: this.route
      })
    );
  }

  addNewItem(): void {
    this.router.navigateWithPersistedState(["add-item"], { relativeTo: this.route });
  }

  reviewAndSign(): void {
    this.router.navigateWithPersistedState(["review-and-sign"], { relativeTo: this.route });
  }
}
